<template>
  <div
    class="infinite-scroll-table"
    :class="{
      'infinite-scroll-table--resizable': enableColumnResize,
      'infinite-scroll-table--body-scroll': bodyOnlyScroll,
    }">
    <v-data-table
      ref="dataTable"
      :class="dataTableClass"
      :style="dataTableStyle"
      v-bind="$attrs"
      v-on="listenersForDataTable"
      :items="items"
      :loading="loading"
      :sort-by="localSortBy"
      :sort-desc="localSortDesc"
      hide-default-footer
      hide-default-header
      fixed-header
      disable-pagination
      mobile-breakpoint="0">
      <template v-slot:header="{ props, on }">
        <thead class="v-data-table-header">
          <tr>
            <th
              v-for="(header, index) in props.headers"
              :key="getHeaderKey(header, index)"
              :class="[header.class, { 'infinite-scroll-header-cell--resizable': isColumnResizable(header) }]"
              :style="enableColumnResize ? getHeaderStyle(header, index) : header.style"
              :aria-sort="isSelectionHeader(header) ? null : getAriaSort(header)"
              :tabindex="header.sortable !== false && !isSelectionHeader(header) ? 0 : -1"
              :data-header-key="getHeaderKey(header, index)"
              @click="toggleSort(header)"
              @keyup.enter="toggleSort(header)"
              @keyup.space.prevent="toggleSort(header)">
              <div
                class="d-flex align-center infinite-scroll-header-cell"
                :style="header.sortable !== false ? 'cursor: pointer;' : ''">
                <template v-if="isSelectionHeader(header) && !props.singleSelect">
                  <v-simple-checkbox
                    class="v-data-table__checkbox"
                    :value="props.everyItem"
                    :indeterminate="!props.everyItem && props.someItems"
                    :color="props.checkboxColor || ''"
                    @click.stop
                    @input="toggleSelectAll(on, $event)"
                  />
                </template>
                <template v-else>
                  <v-tooltip
                    bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <span v-on="on" v-bind="attrs" class="infinite-scroll-header-text">
                        {{ header.text }}
                      </span>
                    </template>
                    <span>
                      {{ header.text }}
                    </span>
                  </v-tooltip>
                  <v-icon
                    v-if="header.sortable !== false && getAriaSort(header) !== 'none'"
                    small
                    class="ml-2">
                    {{ getSortIcon(header) }}
                  </v-icon>
                </template>
              </div>
              <div
                v-if="isColumnResizable(header)"
                class="infinite-scroll-header-resizer"
                @mousedown.stop.prevent="startResize(header, index, $event)"
                @click.stop.prevent />
            </th>
          </tr>
        </thead>
      </template>
      <template
        v-for="slotName in passThroughSlots"
        v-slot:[slotName]="slotScope">
        <slot
          :name="slotName"
          v-bind="slotScope" />
      </template>

      <template v-slot:body.append="slotScope">
        <slot
          name="body.append"
          v-bind="slotScope" />
        <tr
          class="infinite-scroll-table__sentinel-row"
          v-show="hasMore"
          style="height: 20px; max-height: 20px;">
          <td :colspan="getColspan(slotScope)">
            <div
              ref="sentinel"
              class="infinite-scroll-table__sentinel-anchor" />
          </td>
        </tr>
      </template>
    </v-data-table>

    <div class="infinite-scroll-table__sentinel">
      <transition name="fade">
        <v-progress-circular
          v-if="hasMore && loading"
          class="mb-1"
          indeterminate
          color="primary"
          size="24" />
      </transition>
    </div>
  </div>
</template>

<script>
export default {
  name: 'InfiniteScroll',
  inheritAttrs: false,
  props: {
    items: {
      type: Array,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
    hasMore: {
      type: Boolean,
      default: true,
    },
    autoLoadOnMount: {
      type: Boolean,
      default: true,
    },
    threshold: {
      type: Number,
      default: 0.25,
    },
    rootMargin: {
      type: String,
      default: '0px 0px 200px 0px',
    },
    triggerPercent: {
      type: Number,
      default: 0.9,
    },
    sortBy: {
      type: Array,
      default: () => [],
    },
    sortDesc: {
      type: Array,
      default: () => [],
    },
    enableColumnResize: {
      type: Boolean,
      default: false,
    },
    fitResizableColumnsToWrapper: {
      type: Boolean,
      default: false,
    },
    resizableColumns: {
      type: Array,
      default: () => [],
    },
    minColumnWidth: {
      type: Number,
      default: 50,
    },
    fitAvailableViewport: {
      type: Boolean,
      default: false,
    },
    viewportBottomOffset: {
      type: Number,
      default: 16,
    },
    viewportMinHeight: {
      type: Number,
      default: 240,
    },
    bodyOnlyScroll: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    observer: null,
    localSortBy: [],
    localSortDesc: [],
    scrollContainer: null,
    columnWidths: {},
    resizeState: null,
    hasUserResizedColumns: false,
    viewportWrapperHeight: null,
    loadStartItemsLength: 0,
    loadMoreRequestPending: false,
    loadingStartedFromLoadMore: false,
    loadMoreSuspended: false,
  }),
  computed: {
    listenersForDataTable() {
      const listeners = { ...this.$listeners };
      delete listeners['update:options'];
      return listeners;
    },
    dataTableClass() {
      return this.fitAvailableViewport
        ? 'infinite-scroll-table__data-table--viewport'
        : 'default-infinite-scroll-height';
    },
    dataTableStyle() {
      if (!this.fitAvailableViewport || this.viewportWrapperHeight === null) return null;

      return { '--infinite-scroll-wrapper-height': `${this.viewportWrapperHeight}px` };
    },
    passThroughSlots() {
      return Object.keys(this.$scopedSlots || {}).filter(
        (slotName) => slotName !== 'body.append',
      );
    },
    normalizedHeaders() {
      const { headers } = this.$attrs;
      return Array.isArray(headers) ? headers : [];
    },
    shouldFitResizableColumnsToWrapper() {
      return this.fitResizableColumnsToWrapper
        && !this.hasUserResizedColumns
        && Array.isArray(this.items)
        && this.items.length === 0;
    },
  },
  watch: {
    hasMore(newValue) {
      if (!newValue) {
        this.resumeLoadMore();
        this.disconnectObserver();
      } else {
        this.resumeLoadMore();
        this.$nextTick(() => {
          this.connectObserver();
        });
      }
    },
    loading(newValue, oldValue) {
      if (newValue && !oldValue) {
        this.loadStartItemsLength = this.getItemsLength();
        this.loadingStartedFromLoadMore = this.loadMoreRequestPending;
        this.loadMoreRequestPending = false;
        return;
      }

      if (oldValue && !newValue && this.hasMore) {
        const startedFromLoadMore = this.loadingStartedFromLoadMore;
        const loadedMoreItems = this.getItemsLength() > this.loadStartItemsLength;
        if (startedFromLoadMore && !loadedMoreItems) {
          this.loadMoreSuspended = true;
          this.disconnectObserver();
          this.loadingStartedFromLoadMore = false;
          return;
        }

        this.resumeLoadMore();
        this.loadingStartedFromLoadMore = false;
        this.$nextTick(() => {
          this.reconnectObserver();
          this.requestMoreIfNearEnd();
          if (this.bodyOnlyScroll) {
            this.syncBodyScrollLayout();
          }
        });
      }
    },
    items(newItems = [], oldItems = []) {
      if (newItems.length !== oldItems.length) {
        this.resumeLoadMore();
      }

      this.$nextTick(() => {
        if (
          this.enableColumnResize
          && this.fitResizableColumnsToWrapper
          && !this.hasUserResizedColumns
        ) {
          this.resetColumnWidths();
          this.syncColumnWidths();
        }
        this.refreshScrollContainer();
        if (this.bodyOnlyScroll) {
          this.syncBodyScrollLayout();
        }
      });
    },
    sortBy: {
      handler(val = []) {
        this.resumeLoadMore();
        this.localSortBy = Array.isArray(val) ? [...val] : [];
      },
      immediate: true,
    },
    sortDesc: {
      handler(val = []) {
        this.resumeLoadMore();
        this.localSortDesc = Array.isArray(val) ? [...val] : [];
      },
      immediate: true,
    },
    enableColumnResize(value) {
      if (!value) {
        this.resetColumnWidths();
        this.cleanupResizeEvents();
        return;
      }

      this.$nextTick(() => {
        this.syncColumnWidths();
      });
    },
    normalizedHeaders: {
      handler() {
        if (!this.enableColumnResize) return;
        this.$nextTick(() => {
          this.syncColumnWidths();
        });
      },
      deep: true,
    },
    resizableColumns() {
      if (!this.enableColumnResize) return;
      this.$nextTick(() => {
        this.syncColumnWidths();
      });
    },
    fitAvailableViewport(value) {
      if (!value) {
        this.viewportWrapperHeight = null;
        this.unbindViewportResizeListener();
        return;
      }

      this.$nextTick(() => {
        this.updateViewportWrapperHeight();
      });
      this.bindViewportResizeListener();
    },
    bodyOnlyScroll() {
      this.$nextTick(() => {
        this.refreshScrollContainer();
        this.syncBodyScrollLayout();
      });
    },
  },
  methods: {
    isSelectionHeader(header) {
      return header && header.value === 'data-table-select';
    },
    bindViewportResizeListener() {
      window.addEventListener('resize', this.updateViewportWrapperHeight);
    },
    unbindViewportResizeListener() {
      window.removeEventListener('resize', this.updateViewportWrapperHeight);
    },
    updateViewportWrapperHeight() {
      if (!this.fitAvailableViewport) return;

      const tableRef = this.$refs.dataTable;
      const tableEl = tableRef && tableRef.$el ? tableRef.$el : this.$el;
      const wrapperEl = this.scrollContainer || this.getScrollContainer();
      const targetEl = wrapperEl || tableEl;

      if (!targetEl || typeof targetEl.getBoundingClientRect !== 'function') return;

      const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 0;
      const { top } = targetEl.getBoundingClientRect();
      const nextHeight = Math.max(
        this.viewportMinHeight,
        Math.floor(viewportHeight - top - this.viewportBottomOffset),
      );

      if (nextHeight !== this.viewportWrapperHeight) {
        this.viewportWrapperHeight = nextHeight;
      }
    },
    refreshViewportHeight() {
      this.$nextTick(() => {
        this.refreshScrollContainer();
        this.updateViewportWrapperHeight();
        if (this.bodyOnlyScroll) {
          this.syncBodyScrollLayout();
        }
      });
    },
    getHeaderKey(header, index) {
      if (header && header.value !== undefined && header.value !== null && header.value !== '') {
        return String(header.value);
      }

      return `__col_${index}`;
    },
    isColumnResizable(header) {
      if (!this.enableColumnResize) return false;
      if (this.isSelectionHeader(header)) return false;
      if (!header || header.value === undefined || header.value === null || header.value === '') return false;

      const allowed = Array.isArray(this.resizableColumns) ? this.resizableColumns : [];
      if (!allowed.length) return true;

      return allowed.includes(header.value);
    },
    parseColumnSize(value) {
      if (typeof value === 'number' && Number.isFinite(value)) return value;

      if (typeof value === 'string') {
        const parsed = Number.parseFloat(value);
        if (Number.isFinite(parsed)) return parsed;
      }

      return null;
    },
    getColumnWidthBounds(header) {
      const headerMinWidth = this.parseColumnSize(header && header.minWidth);
      const headerMaxWidth = this.parseColumnSize(header && header.maxWidth);

      const minWidth = Math.max(this.minColumnWidth, headerMinWidth || 0);
      const maxWidth = headerMaxWidth && headerMaxWidth >= minWidth ? headerMaxWidth : null;

      return {
        minWidth,
        maxWidth,
      };
    },
    clampColumnWidth(width, header) {
      const numericWidth = this.parseColumnSize(width);
      if (!numericWidth) return numericWidth;

      const { minWidth, maxWidth } = this.getColumnWidthBounds(header);
      if (maxWidth !== null) {
        return Math.min(Math.max(numericWidth, minWidth), maxWidth);
      }

      return Math.max(numericWidth, minWidth);
    },
    getHeaderWidth(header) {
      const width = this.parseColumnSize(header && header.width);
      if (width !== null) return width;

      const { style } = header || {};
      if (style && typeof style === 'object') {
        return this.parseColumnSize(style.width);
      }

      return null;
    },
    getHeaderStyle(header, index) {
      const styles = [];
      if (header && header.style) styles.push(header.style);

      const explicitWidth = this.getHeaderWidth(header);
      if (explicitWidth !== null) {
        styles.push({
          width: `${explicitWidth}px`,
          minWidth: `${explicitWidth}px`,
        });
      }

      const explicitMinWidth = this.parseColumnSize(header && header.minWidth);
      const explicitMaxWidth = this.parseColumnSize(header && header.maxWidth);
      if (explicitMinWidth !== null || explicitMaxWidth !== null) {
        const { minWidth, maxWidth } = this.getColumnWidthBounds(header);
        styles.push({
          ...(explicitMinWidth !== null ? { minWidth: `${minWidth}px` } : {}),
          ...(explicitMaxWidth !== null && maxWidth !== null ? { maxWidth: `${maxWidth}px` } : {}),
        });
      }

      const key = this.getHeaderKey(header, index);
      const width = this.clampColumnWidth(this.columnWidths[key], header);
      if (width) {
        styles.push({
          width: `${width}px`,
          minWidth: `${width}px`,
        });
      }

      return styles.length ? styles : null;
    },
    getColspan(slotScope) {
      if (
        slotScope
        && Array.isArray(slotScope.headers)
        && slotScope.headers.length
      ) {
        return slotScope.headers.length;
      }

      const { headers } = this.$attrs;
      if (Array.isArray(headers) && headers.length) {
        return headers.length;
      }

      return 1;
    },
    getScrollContainer() {
      const tableRef = this.$refs.dataTable;
      const tableEl = tableRef && tableRef.$el ? tableRef.$el : this.$el;

      if (!tableEl) return null;

      const wrapper = tableEl.querySelector('.v-data-table__wrapper');
      if (!wrapper) return null;

      if (this.bodyOnlyScroll) {
        return wrapper.querySelector('tbody') || wrapper;
      }

      return wrapper;
    },
    refreshScrollContainer() {
      const nextContainer = this.getScrollContainer();
      if (nextContainer === this.scrollContainer) return;

      this.detachScrollListener();
      this.disconnectObserver();

      this.scrollContainer = nextContainer;
      this.attachScrollListener();
      this.connectObserver();
    },
    getHeaderCells() {
      const tableRef = this.$refs.dataTable;
      const tableEl = tableRef && tableRef.$el ? tableRef.$el : this.$el;
      if (!tableEl) return [];

      return Array.from(tableEl.querySelectorAll('thead th'));
    },
    getColgroupCols() {
      const tableRef = this.$refs.dataTable;
      const tableEl = tableRef && tableRef.$el ? tableRef.$el : this.$el;
      if (!tableEl) return [];

      return Array.from(tableEl.querySelectorAll('colgroup col'));
    },
    getHeaderKeysFromDom() {
      return this.getHeaderCells().map((cell, index) => cell.dataset.headerKey || `__col_${index}`);
    },
    fitColumnWidthsToWrapper(nextWidths, headerCells) {
      if (!this.shouldFitResizableColumnsToWrapper) return nextWidths;

      const wrapper = this.scrollContainer || this.getScrollContainer();
      const wrapperWidth = wrapper ? wrapper.clientWidth : 0;
      if (!wrapperWidth) return nextWidths;

      const totalWidth = Object.keys(nextWidths)
        .reduce((total, key) => total + nextWidths[key], 0);
      const overflow = totalWidth - wrapperWidth;
      if (overflow <= 0) return nextWidths;

      const shrinkableColumns = [];
      headerCells.forEach((cell, index) => {
        const header = this.normalizedHeaders[index];
        if (!this.isColumnResizable(header)) return;

        const key = cell.dataset.headerKey || `__col_${index}`;
        const width = nextWidths[key];
        if (!width) return;

        const { minWidth } = this.getColumnWidthBounds(header);
        const shrinkCapacity = width - minWidth;
        if (shrinkCapacity > 0) {
          shrinkableColumns.push({
            key,
            header,
            width,
            shrinkCapacity,
          });
        }
      });

      const totalShrinkCapacity = shrinkableColumns
        .reduce((total, column) => total + column.shrinkCapacity, 0);
      if (totalShrinkCapacity <= 0) return nextWidths;

      const fittedWidths = { ...nextWidths };
      const shrinkTotal = Math.min(overflow, totalShrinkCapacity);
      let remainingShrink = shrinkTotal;

      shrinkableColumns.forEach((column, index) => {
        const isLastColumn = index === shrinkableColumns.length - 1;
        const proportionalShrink = shrinkTotal * (column.shrinkCapacity / totalShrinkCapacity);
        const shrink = isLastColumn ? remainingShrink : Math.min(
          column.shrinkCapacity,
          proportionalShrink,
        );

        remainingShrink = Math.max(remainingShrink - shrink, 0);
        fittedWidths[column.key] = this.clampColumnWidth(column.width - shrink, column.header);
      });

      return fittedWidths;
    },
    syncColumnWidths() {
      if (!this.enableColumnResize) return;

      const headerCells = this.getHeaderCells();
      if (!headerCells.length) return;

      const nextWidths = {};
      headerCells.forEach((cell, index) => {
        const header = this.normalizedHeaders[index];
        const key = cell.dataset.headerKey || `__col_${index}`;
        const existingWidth = this.columnWidths[key];
        const explicitWidth = this.getHeaderWidth(header);
        const measuredWidth = this.shouldFitResizableColumnsToWrapper
          ? cell.getBoundingClientRect().width
          : Math.ceil(cell.getBoundingClientRect().width);
        const nextWidth = explicitWidth !== null ? explicitWidth : measuredWidth;
        const width = this.clampColumnWidth(existingWidth || nextWidth, header);
        if (width) nextWidths[key] = width;
      });

      this.columnWidths = this.fitColumnWidthsToWrapper(nextWidths, headerCells);
      this.applyColumnWidths();
    },
    applyColumnWidths() {
      if (this.enableColumnResize) {
        const cols = this.getColgroupCols();
        if (cols.length) {
          const headerKeys = this.getHeaderKeysFromDom();
          cols.forEach((col, index) => {
            const key = headerKeys[index];
            const width = key ? this.columnWidths[key] : null;
            if (width) {
              col.style.width = `${width}px`;
              col.style.minWidth = `${width}px`;
            } else {
              col.style.width = '';
              col.style.minWidth = '';
            }
          });
        }
      }

      this.$nextTick(() => {
        if (this.bodyOnlyScroll) {
          this.syncBodyScrollLayout();
          return;
        }

        this.syncHeaderHorizontalScroll();
      });
    },
    startResize(header, index, event) {
      if (!this.isColumnResizable(header)) return;
      if (event && event.button !== 0) return;

      const key = this.getHeaderKey(header, index);
      const headerCell = event.currentTarget
        ? (event.currentTarget.closest ? event.currentTarget.closest('th') : event.currentTarget.parentElement)
        : null;
      const rawBaseWidth = this.columnWidths[key] || (headerCell ? headerCell.getBoundingClientRect().width : 0);
      const baseWidth = this.clampColumnWidth(rawBaseWidth, header);
      const { minWidth, maxWidth } = this.getColumnWidthBounds(header);

      this.cleanupResizeEvents();
      this.resizeState = {
        key,
        startX: event.clientX,
        startWidth: baseWidth,
        minWidth,
        maxWidth,
        userSelect: document.body.style.userSelect,
        cursor: document.body.style.cursor,
      };

      document.body.style.userSelect = 'none';
      document.body.style.cursor = 'col-resize';

      window.addEventListener('mousemove', this.handleResizeMove);
      window.addEventListener('mouseup', this.handleResizeEnd);
    },
    handleResizeMove(event) {
      if (!this.resizeState) return;

      const delta = event.clientX - this.resizeState.startX;
      let nextWidth = Math.max(this.resizeState.startWidth + delta, this.resizeState.minWidth || this.minColumnWidth);

      if (this.resizeState.maxWidth !== null && this.resizeState.maxWidth !== undefined) {
        nextWidth = Math.min(nextWidth, this.resizeState.maxWidth);
      }

      this.hasUserResizedColumns = true;
      this.$set(this.columnWidths, this.resizeState.key, nextWidth);
      this.applyColumnWidths();
    },
    handleResizeEnd() {
      this.cleanupResizeEvents();
    },
    cleanupResizeEvents() {
      window.removeEventListener('mousemove', this.handleResizeMove);
      window.removeEventListener('mouseup', this.handleResizeEnd);

      if (!this.resizeState) return;

      document.body.style.userSelect = this.resizeState.userSelect || '';
      document.body.style.cursor = this.resizeState.cursor || '';
      this.resizeState = null;
    },
    resetColumnWidths() {
      this.columnWidths = {};
      this.hasUserResizedColumns = false;
      const cols = this.getColgroupCols();
      cols.forEach((col) => {
        col.style.width = '';
        col.style.minWidth = '';
      });
    },
    attachScrollListener() {
      if (!this.scrollContainer) return;
      this.scrollContainer.addEventListener('scroll', this.handleScroll, { passive: true });

      if (this.bodyOnlyScroll) {
        this.scrollContainer.addEventListener('scroll', this.handleBodyScrollSync, { passive: true });
      }
    },
    detachScrollListener() {
      if (!this.scrollContainer) return;
      this.scrollContainer.removeEventListener('scroll', this.handleScroll);

      if (this.bodyOnlyScroll) {
        this.scrollContainer.removeEventListener('scroll', this.handleBodyScrollSync);
      }
    },
    handleBodyScrollSync() {
      this.syncBodyScrollGutter();
      this.syncHeaderHorizontalScroll();
    },
    getBodyScrollElements() {
      const tableRef = this.$refs.dataTable;
      const tableEl = tableRef && tableRef.$el ? tableRef.$el : this.$el;
      if (!tableEl) return null;

      const wrapper = tableEl.querySelector('.v-data-table__wrapper');
      if (!wrapper) return null;

      const tbody = wrapper.querySelector('tbody');
      const thead = wrapper.querySelector('thead');
      if (!tbody || !thead) return null;

      return { tbody, thead, theadRow: thead.querySelector('tr') };
    },
    getBodyScrollColumnWidths(headerCells) {
      return headerCells.map((cell, index) => {
        const header = this.normalizedHeaders[index];
        const key = cell.dataset.headerKey || `__col_${index}`;
        const fromState = this.columnWidths[key];
        if (fromState) return fromState;

        const fromStyle = this.getHeaderWidth(header);
        if (fromStyle !== null) return fromStyle;

        return Math.ceil(cell.getBoundingClientRect().width);
      });
    },
    expandBodyScrollWidthsToFillContainer(widths, headerCells, containerWidth) {
      const totalWidth = widths.reduce((sum, width) => sum + width, 0);
      if (!containerWidth || totalWidth >= containerWidth) return widths;

      const extra = containerWidth - totalWidth;
      const expandableIndexes = headerCells
        .map((cell, index) => index)
        .filter((index) => this.isColumnResizable(this.normalizedHeaders[index]));

      const expanded = [...widths];

      if (!expandableIndexes.length) {
        expanded[expanded.length - 1] += extra;
        return expanded;
      }

      const expandableTotal = expandableIndexes.reduce((sum, index) => sum + widths[index], 0);
      let remaining = extra;

      expandableIndexes.forEach((index, expandableIndex) => {
        const isLast = expandableIndex === expandableIndexes.length - 1;
        const share = isLast
          ? remaining
          : Math.floor(extra * (widths[index] / expandableTotal));

        expanded[index] += share;
        remaining -= share;
      });

      return expanded;
    },
    applyBodyScrollCellWidths(cells, widths) {
      cells.forEach((cell, index) => {
        const width = widths[index];
        if (!width) return;

        cell.style.width = `${width}px`;
        cell.style.minWidth = `${width}px`;
        cell.style.maxWidth = `${width}px`;
      });
    },
    syncBodyScrollGutter() {
      if (!this.bodyOnlyScroll) return;

      const elements = this.getBodyScrollElements();
      if (!elements) return;

      const { tbody, thead } = elements;
      const scrollbarWidth = tbody.offsetWidth - tbody.clientWidth;
      thead.style.paddingRight = scrollbarWidth > 0 ? `${scrollbarWidth}px` : '';
    },
    syncBodyScrollLayout() {
      if (!this.bodyOnlyScroll) return;

      const elements = this.getBodyScrollElements();
      if (!elements) return;

      const headerCells = this.getHeaderCells();
      if (!headerCells.length) return;

      const { tbody, thead, theadRow } = elements;
      const containerWidth = tbody.clientWidth;
      const widths = this.expandBodyScrollWidthsToFillContainer(
        this.getBodyScrollColumnWidths(headerCells),
        headerCells,
        containerWidth,
      );
      const totalWidth = widths.reduce((sum, width) => sum + width, 0);
      const rowWidth = Math.max(totalWidth, containerWidth);

      this.applyBodyScrollCellWidths(headerCells, widths);

      if (theadRow && rowWidth > 0) {
        theadRow.style.width = `${rowWidth}px`;
        theadRow.style.minWidth = `${rowWidth}px`;
      }

      tbody.querySelectorAll('tr').forEach((row) => {
        if (row.classList.contains('infinite-scroll-table__sentinel-row')) return;

        if (rowWidth > 0) {
          row.style.width = `${rowWidth}px`;
          row.style.minWidth = `${rowWidth}px`;
        }

        this.applyBodyScrollCellWidths(Array.from(row.querySelectorAll('td')), widths);
      });

      this.syncBodyScrollGutter();
      this.syncHeaderHorizontalScroll();
    },
    syncHeaderHorizontalScroll() {
      if (!this.bodyOnlyScroll || !this.scrollContainer) return;

      const tbody = this.scrollContainer;
      const theadRow = tbody?.previousElementSibling?.querySelector('tr');
      if (!theadRow) return;

      const referenceRow = tbody.querySelector('tr:not(.infinite-scroll-table__sentinel-row)');
      if (referenceRow) {
        const rowWidth = Math.max(referenceRow.scrollWidth, referenceRow.getBoundingClientRect().width);
        if (rowWidth > 0) {
          theadRow.style.width = `${rowWidth}px`;
          theadRow.style.minWidth = `${rowWidth}px`;
        }
      }

      theadRow.style.transform = `translateX(-${tbody.scrollLeft}px)`;
    },
    connectObserver() {
      if (this.observer || !this.hasMore) return;
      if (!this.$refs.sentinel) return;
      if (!this.scrollContainer) return;

      this.observer = new IntersectionObserver(
        this.handleIntersect,
        {
          root: this.scrollContainer,
          threshold: this.threshold,
          rootMargin: this.rootMargin,
        },
      );

      this.observer.observe(this.$refs.sentinel);
    },
    reconnectObserver() {
      this.disconnectObserver();
      this.connectObserver();
    },
    disconnectObserver() {
      if (this.observer) {
        this.observer.disconnect();
        this.observer = null;
      }
    },
    getItemsLength() {
      return Array.isArray(this.items) ? this.items.length : 0;
    },
    resumeLoadMore() {
      this.loadMoreSuspended = false;
    },
    handleIntersect(entries) {
      const [entry] = entries;
      if (!entry || !entry.isIntersecting) return;
      this.requestMore();
    },
    handleScroll() {
      this.requestMoreIfNearEnd();
    },
    requestMoreIfNearEnd() {
      if (!this.scrollContainer) return;

      const { scrollTop } = this.scrollContainer;
      const { clientHeight } = this.scrollContainer;
      const { scrollHeight } = this.scrollContainer;

      const scrollable = scrollHeight - clientHeight;
      if (scrollable <= 0) {
        if (this.autoLoadOnMount) this.requestMore();
        return;
      }

      const progress = scrollTop / scrollable;
      if (progress >= this.triggerPercent) {
        this.requestMore();
      }
    },
    requestMore() {
      if (!this.hasMore || this.loading || this.loadMoreSuspended) return;
      this.loadMoreRequestPending = true;
      this.$emit('load-more');
    },
    toggleSort(header) {
      if (this.isSelectionHeader(header)) return;
      if (header.sortable === false) return;

      let sortBy = [...this.localSortBy];
      let sortDesc = [...this.localSortDesc];

      const index = sortBy.indexOf(header.value);
      if (index === -1) {
        sortBy = [header.value];
        sortDesc = [false];
      } else if (sortDesc[index] === false) {
        sortDesc[index] = true;
      } else {
        sortBy.splice(index, 1);
        sortDesc.splice(index, 1);
      }

      this.localSortBy = sortBy;
      this.localSortDesc = sortDesc;
      this.resumeLoadMore();

      const nextOptions = {
        ...(this.$attrs.options || {}),
        sortBy: [...this.localSortBy],
        sortDesc: [...this.localSortDesc],
      };

      this.$emit('update:options', nextOptions);
      this.$emit('update:sort-by', [...this.localSortBy]);
      this.$emit('update:sort-desc', [...this.localSortDesc]);
    },
    toggleSelectAll(on, value) {
      if (on && typeof on['toggle-select-all'] === 'function') {
        on['toggle-select-all'](value);
      }
    },
    getSortIcon(header) {
      const index = this.localSortBy.indexOf(header.value);

      if (index === -1) return 'mdi-swap-vertical';
      return this.localSortDesc[index] ? 'mdi-arrow-down' : 'mdi-arrow-up';
    },
    getAriaSort(header) {
      const index = this.localSortBy.indexOf(header.value);

      if (index === -1) return 'none';
      return this.localSortDesc[index] ? 'descending' : 'ascending';
    },
  },
  mounted() {
    this.$nextTick(() => {
      this.refreshScrollContainer();
      if (this.enableColumnResize) {
        this.syncColumnWidths();
      }
      if (this.fitAvailableViewport) {
        this.updateViewportWrapperHeight();
      }
      if (this.bodyOnlyScroll) {
        this.syncBodyScrollLayout();
      }
    });

    if (this.fitAvailableViewport) {
      this.bindViewportResizeListener();
    }

    if (this.autoLoadOnMount && this.hasMore && !this.loading) {
      this.$emit('load-more');
    }
  },
  beforeDestroy() {
    this.disconnectObserver();
    this.detachScrollListener();
    this.cleanupResizeEvents();
    this.unbindViewportResizeListener();
  },
};
</script>

<style scoped>
.infinite-scroll-table {
  display: flex;
  flex-direction: column;
  position: relative;
}

::v-deep .v-data-table__wrapper {
  overflow-x: auto;
  overflow-y: auto;
}

::v-deep .v-data-table__wrapper table thead tr {
  position: sticky;
  top: 0;
  z-index: 2;
  background-color: inherit;
}

::v-deep .v-data-table__wrapper table tbody tr:last-child td,
::v-deep .v-data-table__wrapper table tbody > tr.v-data-table__empty-wrapper > td {
  border-bottom: none !important;
}

.infinite-scroll-table--resizable ::v-deep .v-data-table__wrapper table {
  table-layout: fixed;
}

::v-deep .infinite-scroll-table__data-table--viewport > .v-data-table__wrapper {
  max-height: var(--infinite-scroll-wrapper-height) !important;
}

.infinite-scroll-header-cell--resizable {
  position: relative;
}

.infinite-scroll-header-resizer {
  position: absolute;
  top: 0;
  right: 0;
  width: 6px;
  height: 100%;
  cursor: col-resize;
  z-index: 3;
}

.infinite-scroll-header-resizer::after {
  content: '';
  position: absolute;
  top: 6px;
  bottom: 6px;
  left: 2px;
  width: 1px;
  background-color: color-mix(in srgb, var(--v-shadow-base) 20%, transparent);
}

.infinite-scroll-header-cell--resizable:hover .infinite-scroll-header-resizer::after {
  background-color: var(--v-primary-base);
}

.infinite-scroll-table__sentinel {
  display: flex;
  justify-content: center;
  align-items: center;
  position: absolute;
  left: 0;
  right: 0;
  bottom: 4px;
  z-index: 2;
  pointer-events: none;
}

.infinite-scroll-table__sentinel-anchor {
  min-height: 1px;
}

.infinite-scroll-table__sentinel-row td {
  padding: 0;
  border: 0;
}

.infinite-scroll-table--body-scroll ::v-deep .v-data-table__wrapper {
  overflow: hidden;
}

.infinite-scroll-table--body-scroll ::v-deep .v-data-table__wrapper > table {
  display: flex;
  flex-direction: column;
  width: 100%;
}

.infinite-scroll-table--body-scroll ::v-deep .v-data-table__wrapper table thead {
  display: block;
  flex-shrink: 0;
  overflow: hidden;
  width: 100%;
}

.infinite-scroll-table--body-scroll ::v-deep .v-data-table__wrapper table thead tr {
  display: table;
  min-width: 100%;
  position: static;
  table-layout: fixed;
  top: auto;
  z-index: auto;
}

.infinite-scroll-table--body-scroll ::v-deep .v-data-table__wrapper table tbody {
  display: block;
  overflow: auto;
}

.infinite-scroll-table--body-scroll ::v-deep .v-data-table__wrapper table tbody tr {
  display: table;
  min-width: 100%;
  table-layout: fixed;
}

.infinite-scroll-header-cell {
  gap: 4px;
}

.infinite-scroll-header-text {
  display: inline-block;
  max-width: 100%;
  white-space: normal;
  word-break: break-word;
  line-height: 1.2;
  color: var(--v-primary-base);
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}
</style>
